import { Extension, Node, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import type { Extensions } from "@tiptap/core";

// communicatie (Slice 6): the TipTap extension set + custom ProseMirror nodes ported from the Jmix
// editor (src/main/frontend/src/fawkes/comm-editor.js). The CRITICAL contract is the `variable` node:
// it MUST round-trip the {Group.Field} placeholder tokens EXACTLY. The api/ render pipeline and the
// Jmix renderer both parse `\{([A-Za-z0-9_.]+)\}` over the stored bodyHtml, so a pill is serialized as
//   <span data-variable="Group.Field" class="tt-var">{Group.Field}</span>
// — the literal {Group.Field} text lives INSIDE the span, so the token regex always matches it and the
// span wrapper is harmless markup. parseHTML(span[data-variable]) reads it back as a node on load.
//
// The other Jmix custom nodes (page-break, columns, signature, repeat-block) are ported as faithful
// parseHTML/renderHTML round-trips so seeded template HTML (which uses tt-cols / data-repeat / etc.) is
// PRESERVED, never corrupted. The fancy nodeViews (drag-resize image, repeat header chrome) are NOT
// ported in this MVP — but the markup still round-trips. Table + Image extensions are included because
// real seeded templates are table-heavy (213 <table> in the seed HTML); without them TipTap would drop
// that markup on parse and corrupt the document.
//
// FORMATTING ROUND-TRIP (data-loss fix): TextStyle + Color (text colour), FontFamily, the custom
// FontSize mark, and Link are added so that seeded inline markup — <span style="color:…">,
// <span style="font-family:…">, <span style="font-size:…"> and <a href="…"> — survives load→edit→save
// instead of being silently stripped by a schema that lacks a node/mark for it. Underline + TextAlign
// (incl. style="text-align:justify") were already present. None of these touch the {Group.Field} pill.

/* The variable/placeholder pill — THE core communicatie node. Atomic, inline, non-editable. */
export const Variable = Node.create({
  name: "variable",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      token: {
        default: "",
        parseHTML: (el) => (el as HTMLElement).getAttribute("data-variable") ?? "",
      },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-variable]" }];
  },
  renderHTML({ node, HTMLAttributes }) {
    // The literal {token} text MUST be the text content so the api/Jmix token regex finds it.
    return [
      "span",
      mergeAttributes(HTMLAttributes, { "data-variable": node.attrs.token, class: "tt-var" }),
      `{${node.attrs.token}}`,
    ];
  },
  renderText({ node }) {
    return `{${node.attrs.token}}`;
  },
});

export const PageBreak = Node.create({
  name: "pageBreak",
  group: "block",
  atom: true,
  parseHTML() {
    return [{ tag: "div[data-page-break]" }];
  },
  renderHTML() {
    return ["div", { "data-page-break": "true", class: "tt-pagebreak" }, "— pagina-einde —"];
  },
});

export const Column = Node.create({
  name: "column",
  content: "block+",
  isolating: true,
  parseHTML() {
    return [{ tag: "div.tt-col" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { class: "tt-col" }), 0];
  },
});

export const Columns = Node.create({
  name: "columns",
  group: "block",
  content: "column column",
  parseHTML() {
    return [{ tag: "div.tt-cols" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { class: "tt-cols" }), 0];
  },
});

export const Signature = Node.create({
  name: "signature",
  group: "block",
  atom: true,
  addAttributes() {
    return {
      label: {
        default: "Handtekening",
        parseHTML: (el) =>
          (el as HTMLElement).getAttribute("data-label") ||
          ((el as HTMLElement).textContent || "").trim().split("\n")[0] ||
          "Handtekening",
      },
    };
  },
  parseHTML() {
    return [{ tag: "div[data-signature]" }];
  },
  renderHTML({ node }) {
    const lbl = (node.attrs.label as string) || "Handtekening";
    return [
      "div",
      { "data-signature": "true", "data-label": lbl, class: "tt-sign" },
      ["div", { class: "tt-sign-cap" }, lbl],
      ["div", { class: "tt-sign-line" }],
      ["div", { class: "tt-sign-row" }, ["span", {}, "Naam:"], ["span", { class: "tt-sign-fill" }]],
      ["div", { class: "tt-sign-row" }, ["span", {}, "Datum:"], ["span", { class: "tt-sign-fill" }]],
    ];
  },
});

/**
 * Repeat block ("herhaalblok") — ported as a round-tripping container WITHOUT the Jmix nodeView chrome
 * (the per-item header is deferred). The data-repeat attribute is preserved so the api/ render pipeline
 * (which keys repeat sections off `data-repeat="..."`) still works and existing seeded blocks are kept.
 */
export const Repeat = Node.create({
  name: "repeat",
  group: "block",
  content: "block+",
  defining: true,
  addAttributes() {
    return {
      over: {
        default: "Dier",
        parseHTML: (el) => (el as HTMLElement).getAttribute("data-repeat") || "Dier",
        renderHTML: (attrs) => ({ "data-repeat": attrs.over }),
      },
    };
  },
  parseHTML() {
    return [{ tag: "div[data-repeat]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { class: "tt-repeat" }), 0];
  },
});

/** Welk kop- of voettekstblok: de koptekst voor elke pagina, die voor pagina 1, of de voettekst. */
export type KopVoetSoort = "kop" | "kopEerste" | "voet";

const KOPVOET_KLASSE: Record<KopVoetSoort, string> = {
  kop: "db-header",
  kopEerste: "db-header db-header-eerste",
  voet: "db-footer",
};

/**
 * Kop- en voettekst ("header en footer blok"). Dezelfde markup als in de afdruksjablonen met vaste
 * opmaak — `div.db-header`, `div.db-header.db-header-eerste`, `div.db-footer` — zodat de PDF-generator
 * ze in de paginamarge zet en op elke pagina herhaalt. Zonder dit blok kende het schema geen div met een
 * klasse en gooide TipTap de voettekst van een sjabloon bij het openen weg.
 */
export const KopVoet = Node.create({
  name: "kopVoet",
  group: "block",
  content: "block+",
  defining: true,
  isolating: true,
  addAttributes() {
    return {
      soort: {
        default: "kop",
        parseHTML: (el) => {
          const k = (el as HTMLElement).classList;
          if (k.contains("db-footer")) return "voet";
          return k.contains("db-header-eerste") ? "kopEerste" : "kop";
        },
        // De klasse zegt het al; geen extra attribuut in het opgeslagen sjabloon.
        renderHTML: () => ({}),
      },
    };
  },
  parseHTML() {
    return [{ tag: "div.db-header" }, { tag: "div.db-footer" }];
  },
  renderHTML({ node }) {
    return ["div", { class: KOPVOET_KLASSE[node.attrs.soort as KopVoetSoort] ?? "db-header" }, 0];
  },
});

type KopVoetEditor = import("@tiptap/core").Editor;

/** Waar het blok van deze soort staat (van, tot), of null. */
export function kopVoetPositie(editor: KopVoetEditor, soort: KopVoetSoort): { from: number; to: number } | null {
  let gevonden: { from: number; to: number } | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (gevonden) return false;
    if (node.type.name === "kopVoet" && node.attrs.soort === soort) {
      gevonden = { from: pos, to: pos + node.nodeSize };
      return false;
    }
    return true;
  });
  return gevonden;
}

/**
 * Zet een kop- of voettekstblok in het document, of neemt het bestaande, en zet de cursor erin. Een
 * koptekst bovenaan, de voettekst onderaan: zo leest het document in de editor zoals op papier. Waar het
 * staat maakt voor de PDF niet uit, die zet het in de marge.
 */
export function kopVoetInvoegen(editor: KopVoetEditor, soort: KopVoetSoort): void {
  const bestaand = kopVoetPositie(editor, soort);
  if (bestaand) {
    editor.commands.setTextSelection(bestaand.to - 2);
    editor.commands.focus();
    return;
  }
  const plek = soort === "voet" ? editor.state.doc.content.size : 0;
  editor
    .chain()
    .focus()
    .insertContentAt(plek, { type: "kopVoet", attrs: { soort }, content: [{ type: "paragraph" }] })
    .run();
  const nieuw = kopVoetPositie(editor, soort);
  if (nieuw) editor.commands.setTextSelection(nieuw.from + 2);
}

/** Haalt het kop- of voettekstblok van deze soort weg. */
export function kopVoetVerwijderen(editor: KopVoetEditor, soort: KopVoetSoort): void {
  const plek = kopVoetPositie(editor, soort);
  if (plek) editor.commands.deleteRange(plek);
}

/**
 * FontSize — a TextStyle attribute, NOT a separate npm package (TipTap v2.27.x ships no standalone
 * FontSize extension; it only landed as `@tiptap/extension-text-style`'s `FontSize` in v3). Ported
 * faithfully from the Jmix editor (comm-editor.js): the size lives as `style="font-size:…"` on a
 * <span> (the same TextStyle host as color/family), so seeded `<span style="font-size:14px">…</span>`
 * markup round-trips losslessly instead of being stripped.
 */
export const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return { types: ["textStyle"] as string[] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (el: HTMLElement) => el.style.fontSize || null,
            renderHTML: (attrs: { fontSize?: string | null }) =>
              attrs.fontSize ? { style: `font-size:${attrs.fontSize}` } : {},
          },
        },
      },
    ];
  },
  // Commands typed loosely to avoid a global module-augmentation of @tiptap/core's Commands interface
  // (that would be a shared edit); the toolbar calls these through editor.commands with the same names
  // the Jmix editor uses. The `chain` callback arg follows TipTap's CommandProps contract.
  addCommands() {
    type ChainFn = () => {
      setMark: (name: string, attrs: Record<string, unknown>) => ChainResult;
    };
    type ChainResult = {
      run: () => boolean;
      removeEmptyTextStyle: () => ChainResult;
    } & ReturnType<ChainFn>;
    return {
      setFontSize:
        (size: string) =>
        ({ chain }: { chain: ChainFn }) =>
          (chain().setMark("textStyle", { fontSize: size }) as ChainResult).run(),
      unsetFontSize:
        () =>
        ({ chain }: { chain: ChainFn }) =>
          (chain().setMark("textStyle", { fontSize: null }) as ChainResult)
            .removeEmptyTextStyle()
            .run(),
    } as Record<string, unknown>;
  },
});

/** The full extension list used by the editor. */
export function commEditorExtensions(): Extensions {
  return [
    StarterKit,
    Underline,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    // TextStyle hosts the color / font-family / font-size span attributes so seeded inline-styled
    // <span> markup round-trips instead of being dropped on parse. Color/FontFamily/FontSize all
    // attach their attribute to this same `textStyle` mark.
    TextStyle,
    Color,
    FontFamily,
    FontSize,
    // Hyperlinks: openOnClick false (editing, not navigating); autolink keeps pasted URLs as links.
    // HTMLAttributes mirror the Jmix config so saved <a> tags carry rel/target like the legacy editor.
    Link.configure({
      openOnClick: false,
      autolink: true,
      HTMLAttributes: { rel: "noopener", target: "_blank" },
    }),
    Image.configure({ inline: false, allowBase64: true }),
    Table.configure({ resizable: false }),
    TableRow,
    TableHeader,
    TableCell,
    Variable,
    PageBreak,
    Columns,
    Column,
    Signature,
    Repeat,
    KopVoet,
  ];
}

/**
 * Load-time normalization (ported from comm-editor.js `normalizeVariables`). Converts any existing
 * variable spans AND any bare {Token} text in the source HTML into the canonical pill span, so the
 * Variable.parseHTML picks them up as nodes. Idempotent: re-collapses spans before re-wrapping.
 */
export function normalizeVariables(html: string | null | undefined): string {
  if (!html) return "";
  let s = html.replace(/<span[^>]*data-variable[^>]*>(\{[^}]+\})<\/span>/g, "$1");
  s = s.replace(
    /\{([A-Za-z0-9_.]+)\}/g,
    (_m, tok: string) => `<span data-variable="${tok}" class="tt-var">{${tok}}</span>`,
  );
  return s;
}
